import map from 'lodash/map'
import * as React from 'react'
import {
  GenericSelector,
  createSelectorOptionAsItem
} from '@frontrow/console/src/components/generic-selector'
import {CalendarDatePicker} from '@frontrow/console/src/components/calendar-date-picker'
import {Button} from '@frontrow/console/src/components/button'
import {Label} from '@frontrow/console/src/components/ui/label'

import {type LicenseAllocationSubjectT} from '@frontrow/console/src/models/teacher-license-allocation'

type Props = {
  subjects: Array<[LicenseAllocationSubjectT, string]>
  subjectVal: LicenseAllocationSubjectT
  onSubjectChange: (val: LicenseAllocationSubjectT) => void
  startsAtVal: Date
  onStartsAtChange: (val?: Date | null) => void
  expiresAtVal: Date
  onExpiresAtChange: (val?: Date | null) => void
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  onAddAll: () => void
  onCancel: () => void
}

// The shared FormField renders its label without an htmlFor and carries its own
// bottom margin, which left the three controls on three different baselines.
function FormCell(props: {
  id: string
  label: string
  children: React.ReactNode
}): React.ReactElement {
  const {id, label, children} = props
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  )
}

export default function NewLicense(props: Props): React.ReactElement {
  const {
    subjects,
    subjectVal,
    startsAtVal,
    expiresAtVal,
    onSubjectChange,
    onStartsAtChange,
    onExpiresAtChange,
    onSubmit,
    onAddAll,
    onCancel
  } = props

  const subjectsOptions = map(subjects, ([subject, label]) =>
    createSelectorOptionAsItem(subject, label)
  )

  return (
    <form
      onSubmit={onSubmit}
      className="my-4 flex flex-wrap items-end gap-4 rounded border border-border p-4"
    >
      <FormCell id="new-license-subject" label="Subject">
        <GenericSelector
          id="new-license-subject"
          onChangeSelection={onSubjectChange}
          selection={subjectVal}
          collection={subjectsOptions}
        />
      </FormCell>
      <FormCell id="new-license-start-date" label="Start date">
        <CalendarDatePicker
          id="new-license-start-date"
          selectedDate={startsAtVal}
          onSelectDate={onStartsAtChange}
        />
      </FormCell>
      <FormCell id="new-license-end-date" label="Expiration date">
        <CalendarDatePicker
          id="new-license-end-date"
          selectedDate={expiresAtVal}
          onSelectDate={onExpiresAtChange}
          min={startsAtVal}
        />
      </FormCell>
      <div className="flex gap-2">
        <Button style="primary" type="submit" addClass="h-8" dataTest="add-license-button">
          Add license
        </Button>
        <Button
          style="secondary"
          type="button"
          addClass="h-8"
          onClick={onAddAll}
          dataTest="add-all-licenses-button"
        >
          Add all four subjects
        </Button>
        <Button style="link" type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
